import type { Coordinates3D } from './common';
import { formatLocationCode, locationBarcode, type FloorBuilding, type LocationType } from './warehouse';

// Warehouse builder: racks, pallet places, IBC places and floor areas stand on the floor plan at
// their real size, and hold the bins (Koridor-Raf-Kat-Göz) pick routes walk to. A block of rows
// is planned in one go; rows and areas can be moved, turned and resized later.

export interface Range {
  readonly from: number;
  readonly to: number;
}

export const RACK_KINDS = ['shelf', 'pallet_rack', 'floor', 'ibc', 'area'] as const;
export type RackKind = (typeof RACK_KINDS)[number];
/** What stock is stored on (an area is a marked patch of floor instead). */
export type StorageKind = Exclude<RackKind, 'area'>;
export const STORAGE_KINDS = ['shelf', 'pallet_rack', 'floor', 'ibc'] as const satisfies readonly StorageKind[];

/** The way the open side of a rack looks, toward its walkway: 0 = +x, 90 = +y, 180 = −x, 270 = −y. */
export const FACINGS = [0, 90, 180, 270] as const;
export type Facing = (typeof FACINGS)[number];

/** One rack position (bay), pallet place, IBC place or floor area, as it stands on the floor. */
export interface RackGeometry {
  readonly aisle: string;
  readonly rack: number;
  readonly kind: RackKind;
  /** centre of the footprint, metres */
  readonly x: number;
  readonly y: number;
  readonly facing: Facing;
  /** along the open side, metres */
  readonly width: number;
  /** from the open side to the back, metres */
  readonly depth: number;
  /** between shelf levels, metres (0 for floor areas) */
  readonly levelHeight: number;
  /** from the open side to where the picker stands (half the walkway), metres */
  readonly reach: number;
  /** what an area is called besides its code ("Trendyol Express"); not there when it has none */
  readonly name?: string;
}

export interface Footprint {
  readonly x0: number;
  readonly x1: number;
  readonly y0: number;
  readonly y1: number;
}

export const LOCATION_TYPE_LABELS: Readonly<Record<LocationType, string>> = {
  pick: 'Toplama rafı',
  bulk: 'Yedek alan (palet)',
  receiving: 'Mal kabul',
  staging: 'Hazırlık',
  packing: 'Paketleme',
  shipping: 'Sevk',
  returns: 'İade',
  quarantine: 'Karantina',
  damaged: 'Hasarlı',
};

export const RACK_KIND_LABELS: Readonly<Record<RackKind, string>> = {
  shelf: 'Raf',
  pallet_rack: 'Palet rafı',
  floor: 'Yer paleti',
  ibc: 'IBC tank',
  area: 'Alan',
};

/** A label inside a sentence: "Paketleme masası" → "paketleme masası", "İade alanı" → "iade alanı"; "IBC tank" stays. */
export const lowerLabel = (label: string): string => (/^\p{Lu}\p{Ll}/u.test(label) ? label.charAt(0).toLocaleLowerCase('tr') + label.slice(1) : label);

/** Sensible sizes of each kind; every value can be changed in the builder. */
export interface RackPreset {
  readonly kind: StorageKind;
  readonly label: string;
  readonly hint: string;
  /** what one position of a row is called: "8 raf", "12 palet yeri" */
  readonly unit: string;
  readonly type: 'pick' | 'bulk';
  readonly width: number;
  readonly depth: number;
  readonly levelHeight: number;
  readonly levels: number;
  readonly bins: number;
  readonly gap: number;
  readonly walkway: number;
  readonly maxWeightKg: number;
}

export const RACK_PRESETS: Readonly<Record<StorageKind, RackPreset>> = {
  shelf: {
    kind: 'shelf',
    label: 'Raf',
    hint: 'Koli ve tek ürün için hafif raf; elle toplanır',
    unit: 'raf',
    type: 'pick',
    width: 1,
    depth: 0.5,
    levelHeight: 0.4,
    levels: 5,
    bins: 3,
    gap: 0,
    walkway: 1.2,
    maxWeightKg: 50,
  },
  pallet_rack: {
    kind: 'pallet_rack',
    label: 'Palet rafı',
    hint: 'Forkliftle yüklenen ağır raf; her göz bir palet',
    unit: 'raf',
    type: 'bulk',
    width: 2.7,
    depth: 1.1,
    levelHeight: 1.6,
    levels: 3,
    bins: 3,
    gap: 0.1,
    walkway: 3,
    maxWeightKg: 1000,
  },
  floor: {
    kind: 'floor',
    label: 'Yer paleti',
    hint: 'Rafsız, yere konan palet yerleri (blok istif)',
    unit: 'palet yeri',
    type: 'bulk',
    width: 0.9,
    depth: 1.2,
    levelHeight: 1.5,
    levels: 1,
    bins: 1,
    gap: 0.1,
    walkway: 3,
    maxWeightKg: 1000,
  },
  ibc: {
    kind: 'ibc',
    label: 'IBC tank',
    hint: '1000 litrelik IBC tank yerleri',
    unit: 'IBC yeri',
    type: 'bulk',
    width: 1.1,
    depth: 1.2,
    levelHeight: 1.2,
    levels: 1,
    bins: 1,
    gap: 0.1,
    walkway: 3,
    maxWeightKg: 1300,
  },
};

export const AREA_TYPES = ['receiving', 'packing', 'shipping', 'staging', 'returns', 'quarantine', 'damaged'] as const satisfies readonly LocationType[];
export type AreaType = (typeof AREA_TYPES)[number];

export interface AreaPreset {
  readonly type: AreaType;
  readonly label: string;
  /** letters of the area's codes: PK-01, PK-02 … */
  readonly code: string;
  readonly hint: string;
  readonly width: number;
  readonly depth: number;
}

export const AREA_PRESETS: Readonly<Record<AreaType, AreaPreset>> = {
  receiving: {
    type: 'receiving',
    label: 'Mal kabul alanı',
    code: 'KB',
    hint: 'Gelen kolilerin sayılıp okutulduğu yer; ürünler yerleştirilene kadar burada bekler.',
    width: 4,
    depth: 3,
  },
  packing: {
    type: 'packing',
    label: 'Paketleme masası',
    code: 'PK',
    hint: 'Her masa kendi numarasını alır (PK-01, PK-02). Aynı kodla paketleme istasyonu da açılır; masanın yazıcısı ve performansı bu numarayla izlenir.',
    width: 1.6,
    depth: 0.8,
  },
  shipping: {
    type: 'shipping',
    label: 'Sevk alanı',
    code: 'SV',
    hint: 'Paketlenen kolilerin kargo firmasını beklediği yer. Her kargo firmasına ayrı numara verin (SV-01 Trendyol Express, SV-02 HepsiJet); koliler karışmaz.',
    width: 3,
    depth: 2,
  },
  staging: {
    type: 'staging',
    label: 'Hazırlık alanı',
    code: 'HZ',
    hint: 'Toplanan siparişlerin paketlemeden önce beklediği yer.',
    width: 3,
    depth: 2,
  },
  returns: {
    type: 'returns',
    label: 'İade alanı',
    code: 'IA',
    hint: 'Müşteri iadelerinin açılıp kontrol edildiği yer.',
    width: 2,
    depth: 2,
  },
  quarantine: {
    type: 'quarantine',
    label: 'Karantina',
    code: 'KR',
    hint: 'Satışa kapalı, incelenecek ürünler.',
    width: 2,
    depth: 2,
  },
  damaged: {
    type: 'damaged',
    label: 'Hasarlı ürün alanı',
    code: 'HS',
    hint: 'Hasarlı gelen veya bozulan ürünler; satışa açılmaz.',
    width: 2,
    depth: 2,
  },
};

interface BlockBase {
  /** the corner of the block with the smallest x and y, metres */
  readonly origin: { readonly x: number; readonly y: number };
  /** rows run along this axis; racks are numbered along it */
  readonly aisleAxis: 'x' | 'y';
  /** of one position along the row, metres */
  readonly width: number;
  /** from the open side to the back, metres */
  readonly depth: number;
  /** between neighbouring positions of a row, metres */
  readonly gap: number;
  readonly zone?: string | null;
}

/** Rows of racks, pallet places or IBC places: one letter per row, positions numbered along it. */
export interface StorageBlockInput extends BlockBase {
  readonly kind: StorageKind;
  readonly type: LocationType;
  /** row letters, first row at the origin */
  readonly aisles: readonly string[];
  readonly racks: Range;
  readonly levels: Range;
  readonly bins: Range;
  readonly levelHeight: number;
  /** width of the walkway in front of a row, metres */
  readonly walkway: number;
  /** between two rows standing back to back, metres */
  readonly backGap: number;
  /** paired: two rows face each other across one walkway; single: every row has its own */
  readonly arrangement: 'paired' | 'single';
  /** the first row faces away from the origin instead of toward the block */
  readonly flip: boolean;
  readonly maxVolumeCm3: number | null;
  readonly maxWeightG: number | null;
}

/** Floor areas in a line: packing tables PK-01 … PK-03, a dock, a returns corner. */
export interface AreaBlockInput extends BlockBase {
  readonly kind: 'area';
  readonly type: AreaType;
  readonly aisle: string;
  /** the area numbers */
  readonly racks: Range;
}

export type BlockInput = StorageBlockInput | AreaBlockInput;

export interface PlannedLocation {
  readonly aisle: string;
  readonly rack: number;
  readonly level: number;
  readonly bin: number;
  readonly code: string;
  readonly barcode: string;
  readonly type: LocationType;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly maxVolumeCm3: number | null;
  readonly maxWeightG: number | null;
}

export interface BlockPlan {
  readonly racks: readonly RackGeometry[];
  readonly bins: readonly PlannedLocation[];
  /** the floor the block takes, walkways included */
  readonly extent: Footprint;
}

/** One builder run may create at most this many bins. */
export const MAX_BLOCK_LOCATIONS = 5_000;

const round3 = (v: number) => Math.round(v * 1000) / 1000;
const size = (r: Range) => Math.max(0, r.to - r.from + 1);
const CODE = /^[A-Z]{1,3}$/;

/** "A".."D" → A, B, C, D; "Y".."AB" → Y, Z, AA, AB. */
export function aisleRange(from: string, to: string): string[] {
  if (!/^[A-Z]{1,3}$/i.test(from) || !/^[A-Z]{1,3}$/i.test(to)) return [];
  const a = aisleNumber(from.toUpperCase());
  const b = aisleNumber(to.toUpperCase());
  if (b < a) return [];
  return Array.from({ length: b - a + 1 }, (_, i) => aisleName(a + i));
}

const aisleNumber = (s: string) => [...s].reduce((n, c) => n * 26 + (c.charCodeAt(0) - 64), 0);
const aisleName = (n: number): string => (n <= 26 ? String.fromCharCode(64 + n) : aisleName(Math.floor((n - 1) / 26)) + String.fromCharCode(65 + ((n - 1) % 26)));
const LAST_AISLE = aisleNumber('ZZZ');

/** Where a row letter stands in A, B, … Z, AA, AB …: 1 for A; 0 for anything else. */
export const aisleIndex = (aisle: string): number => (CODE.test(aisle) ? aisleNumber(aisle) : 0);
/** The row letter at a place in A, B, … Z, AA, AB … (1 = A). */
export const aisleAt = (index: number): string => aisleName(index);

/** A row letter or area code in use in a warehouse: the builder does not give it out again. */
export interface AisleUse {
  readonly aisle: string;
  /** rows of racks, pallet or IBC places, or floor areas ('area') */
  readonly kind: RackKind;
  /** what its places are: "Raf", "Yer paleti", "Paketleme masası", "Sevk alanı" */
  readonly label: string;
  readonly bins: number;
  /** bins switched off; a row with every bin off is not on the map but keeps its letter */
  readonly inactive: number;
  /** the highest rack or area number in use (PK-03 → 3): new areas are numbered after it */
  readonly lastRack: number;
}

export interface RowLetters {
  /** fewer than asked for only when the letters ran out */
  readonly letters: readonly string[];
  /** with a chosen first letter: the letter in use that is in the way (no letters then) */
  readonly blockedBy: string | null;
}

/**
 * Letters for `count` new rows, one after another (A, B, … Z, AA, AB …): the first free ones, or
 * those from a chosen first letter. A letter in use by anything (an area named with a row letter
 * on the old screen, a row switched off) is never given again: the rows of a block pass it by
 * together, and with a chosen first letter the one in the way is named instead.
 */
export function rowLetters(uses: readonly Pick<AisleUse, 'aisle'>[], count: number, start = ''): RowLetters {
  const taken = new Set(uses.map((u) => u.aisle));
  const first = aisleIndex(start);
  if (first > 0) {
    const letters: string[] = [];
    for (let n = first; letters.length < count && n <= LAST_AISLE; n++) {
      const name = aisleName(n);
      if (taken.has(name)) return { letters: [], blockedBy: name };
      letters.push(name);
    }
    return { letters, blockedBy: null };
  }
  let run: string[] = [];
  for (let n = 1; run.length < count && n <= LAST_AISLE; n++) {
    const name = aisleName(n);
    run = taken.has(name) ? [] : [...run, name];
  }
  return { letters: run, blockedBy: null };
}

const NORMAL: Readonly<Record<Facing, { readonly x: number; readonly y: number }>> = {
  0: { x: 1, y: 0 },
  90: { x: 0, y: 1 },
  180: { x: -1, y: 0 },
  270: { x: 0, y: -1 },
};

/** The floor a rack stands on. */
export function rackFootprint(r: Pick<RackGeometry, 'x' | 'y' | 'facing' | 'width' | 'depth'>): Footprint {
  // a rack looking along y has its open side, and so its width, along x
  const sideways = r.facing === 90 || r.facing === 270;
  const hx = (sideways ? r.width : r.depth) / 2;
  const hy = (sideways ? r.depth : r.width) / 2;
  return { x0: round3(r.x - hx), x1: round3(r.x + hx), y0: round3(r.y - hy), y1: round3(r.y + hy) };
}

/** Where a picker stands to reach a level of the rack: the bins' coordinates. Areas: their middle. */
export function pickPoint(r: RackGeometry, level: number): Coordinates3D {
  if (r.kind === 'area') return { x: round3(r.x), y: round3(r.y), z: 0 };
  const n = NORMAL[r.facing];
  const out = r.depth / 2 + r.reach;
  return { x: round3(r.x + n.x * out), y: round3(r.y + n.y * out), z: round3(Math.max(0, level - 1) * r.levelHeight) };
}

/** Litres one bin holds: its share of the shelf times the level height, rounded to 5 litres. */
export function suggestedBinLitres(width: number, depth: number, levelHeight: number, binsPerLevel: number): number | null {
  const litres = (width / Math.max(1, binsPerLevel)) * depth * levelHeight * 1000;
  return Number.isFinite(litres) && litres > 0 ? Math.max(1, Math.round(litres / 5) * 5) : null;
}

const finite = (v: number) => Number.isFinite(v);
const between = (v: number, min: number, max: number) => finite(v) && v >= min && v <= max;

/** Why a block cannot be built (empty = fine). */
export function blockProblems(input: BlockInput): string[] {
  const problems: string[] = [];
  const ranges: (readonly [string, Range])[] = [[input.kind === 'area' ? 'Alan numarası' : 'Raf', input.racks]];
  if (input.kind === 'area') {
    if (!CODE.test(input.aisle)) problems.push(`Alan kodu 1–3 büyük harf olmalı: ${input.aisle}`);
    if (!(AREA_TYPES as readonly string[]).includes(input.type)) problems.push('Alan türü geçersiz');
  } else {
    if (input.aisles.length === 0) problems.push('En az bir sıra gerekli');
    for (const a of input.aisles) if (!CODE.test(a)) problems.push(`Sıra harfi 1–3 büyük harf olmalı: ${a}`);
    if (new Set(input.aisles).size !== input.aisles.length) problems.push('Aynı sıra harfi iki kez yazılmış');
    if (input.type !== 'pick' && input.type !== 'bulk') problems.push('Raflar toplama rafı veya yedek alan olabilir; kabul, paketleme ve diğerleri alan olarak eklenir');
    ranges.push(['Kat', input.levels], ['Göz', input.bins]);
    if (!between(input.levelHeight, 0, 20) || (input.levels.to > input.levels.from && input.levelHeight <= 0)) problems.push('Kat yüksekliği 0–20 m arasında olmalı');
    if (!(input.walkway > 0 && input.walkway <= 50)) problems.push('Koridor genişliği 0–50 m arasında olmalı');
    if (!between(input.backGap, 0, 20)) problems.push('Sırt sırta sıralar arası 0–20 m arasında olmalı');
    if (input.maxVolumeCm3 !== null && !(Number.isInteger(input.maxVolumeCm3) && input.maxVolumeCm3 > 0)) problems.push('Göz hacmi pozitif olmalı');
    if (input.maxWeightG !== null && !(Number.isInteger(input.maxWeightG) && input.maxWeightG > 0)) problems.push('Göz taşıma kapasitesi pozitif olmalı');
  }
  for (const [label, r] of ranges) {
    if (!Number.isInteger(r.from) || !Number.isInteger(r.to) || r.from < 1 || r.to > 99 || r.to < r.from) problems.push(`${label} numaraları 1–99 arasında ve küçükten büyüğe olmalı`);
  }
  if (!(input.width > 0 && input.width <= 50)) problems.push('Genişlik 0–50 m arasında olmalı');
  if (!(input.depth > 0 && input.depth <= 50)) problems.push('Derinlik 0–50 m arasında olmalı');
  if (!between(input.gap, 0, 20)) problems.push('Aradaki boşluk 0–20 m arasında olmalı');
  if (!between(input.origin.x, -10_000, 10_000) || !between(input.origin.y, -10_000, 10_000)) problems.push('Başlangıç noktası sayı olmalı');
  const count = input.kind === 'area' ? size(input.racks) : input.aisles.length * size(input.racks) * size(input.levels) * size(input.bins);
  if (count > MAX_BLOCK_LOCATIONS) problems.push(`Bir seferde en fazla ${MAX_BLOCK_LOCATIONS} göz oluşturulabilir (${count} istendi)`);
  return problems;
}

/**
 * The racks and bins of a block. Rows run along the aisle axis from the origin, positions
 * numbered from it; rows stand side by side across it, the first one at the origin. A row's
 * open side looks at its walkway: paired rows share one (A faces B, B and C stand back to back,
 * C faces D …), single rows each have their own. The bins of one shelf share the spot in the
 * middle of the walkway where the picker stands.
 */
export function planBlock(input: BlockInput): BlockPlan {
  const toXY = (along: number, across: number) =>
    input.aisleAxis === 'y' ? { x: round3(input.origin.x + across), y: round3(input.origin.y + along) } : { x: round3(input.origin.x + along), y: round3(input.origin.y + across) };
  const facingOf = (sign: 1 | -1): Facing => (input.aisleAxis === 'y' ? (sign > 0 ? 0 : 180) : sign > 0 ? 90 : 270);
  const count = size(input.racks);
  const length = count * input.width + Math.max(0, count - 1) * input.gap;
  const alongCentre = (rack: number) => (rack - input.racks.from) * (input.width + input.gap) + input.width / 2;

  if (input.kind === 'area') {
    const racks: RackGeometry[] = [];
    const bins: PlannedLocation[] = [];
    for (let rack = input.racks.from; rack <= input.racks.to; rack++) {
      const at = toXY(alongCentre(rack), input.depth / 2);
      const geometry: RackGeometry = { aisle: input.aisle, rack, kind: 'area', x: at.x, y: at.y, facing: facingOf(-1), width: input.width, depth: input.depth, levelHeight: 0, reach: 0 };
      racks.push(geometry);
      const address = { aisle: input.aisle, rack, level: 1, bin: 1 };
      bins.push({ ...address, code: formatLocationCode(address), barcode: locationBarcode(address), type: input.type, ...pickPoint(geometry, 1), maxVolumeCm3: null, maxWeightG: null });
    }
    return { racks, bins, extent: extentOf(toXY(0, 0), toXY(length, input.depth)) };
  }

  const signs = input.aisles.map((_, i): 1 | -1 => (input.arrangement === 'single' ? (input.flip ? -1 : 1) : (i % 2 === 0) !== input.flip ? 1 : -1));
  const starts: number[] = [];
  let cursor = 0;
  signs.forEach((sign, i) => {
    const before = signs[i - 1];
    // a row looking back needs a walkway before it, unless the row before looks at the same one
    if (sign < 0 && before !== 1) cursor += input.walkway;
    // two rows standing back to back
    if (sign > 0 && before === -1) cursor += input.backGap;
    starts.push(cursor);
    cursor += input.depth;
    if (sign > 0) cursor += input.walkway;
  });

  const racks: RackGeometry[] = [];
  const bins: PlannedLocation[] = [];
  input.aisles.forEach((aisle, i) => {
    const sign = signs[i]!;
    for (let rack = input.racks.from; rack <= input.racks.to; rack++) {
      const at = toXY(alongCentre(rack), starts[i]! + input.depth / 2);
      const geometry: RackGeometry = {
        aisle,
        rack,
        kind: input.kind,
        x: at.x,
        y: at.y,
        facing: facingOf(sign),
        width: input.width,
        depth: input.depth,
        levelHeight: input.levelHeight,
        reach: round3(input.walkway / 2),
      };
      racks.push(geometry);
      for (let level = input.levels.from; level <= input.levels.to; level++) {
        const spot = pickPoint(geometry, level);
        for (let bin = input.bins.from; bin <= input.bins.to; bin++) {
          const address = { aisle, rack, level, bin };
          bins.push({ ...address, code: formatLocationCode(address), barcode: locationBarcode(address), type: input.type, ...spot, maxVolumeCm3: input.maxVolumeCm3, maxWeightG: input.maxWeightG });
        }
      }
    }
  });
  return { racks, bins, extent: extentOf(toXY(0, 0), toXY(length, cursor)) };
}

function extentOf(a: { x: number; y: number }, b: { x: number; y: number }): Footprint {
  return { x0: Math.min(a.x, b.x), x1: Math.max(a.x, b.x), y0: Math.min(a.y, b.y), y1: Math.max(a.y, b.y) };
}

/** Whether two footprints share floor (touching edges do not count). */
export function footprintsOverlap(a: Footprint, b: Footprint, tolerance = 0.005): boolean {
  return a.x0 < b.x1 - tolerance && b.x0 < a.x1 - tolerance && a.y0 < b.y1 - tolerance && b.y0 < a.y1 - tolerance;
}

// --- buildings on the floor plan (office, canteen): drawn, hold no stock ----------------------------

export const MAX_BUILDINGS = 30;
export const BUILDING_DEFAULTS = { name: 'Ofis', width: 8, depth: 5, height: 3 } as const;

/** The floor a building stands on. */
export function buildingFootprint(b: Pick<FloorBuilding, 'x' | 'y' | 'width' | 'depth'>): Footprint {
  return { x0: round3(b.x - b.width / 2), x1: round3(b.x + b.width / 2), y0: round3(b.y - b.depth / 2), y1: round3(b.y + b.depth / 2) };
}

/** Why a building cannot be stored (empty = fine). */
export function buildingProblems(b: FloorBuilding): string[] {
  const out: string[] = [];
  if (!/^[a-z0-9-]{1,24}$/.test(b.id)) out.push('Bina kimliği geçersiz');
  const name = b.name.trim();
  if (name.length < 1 || name.length > 40) out.push('Bina adı 1–40 karakter olmalı');
  if (![b.x, b.y].every((v) => Number.isFinite(v) && Math.abs(v) <= 10_000)) out.push(`${name || 'Bina'}: konum sayı olmalı`);
  if (!(b.width >= 0.5 && b.width <= 300) || !(b.depth >= 0.5 && b.depth <= 300)) out.push(`${name || 'Bina'}: genişlik ve derinlik 0,5–300 m olmalı`);
  if (!(b.height >= 0.5 && b.height <= 30)) out.push(`${name || 'Bina'}: yükseklik 0,5–30 m olmalı`);
  return out;
}

/**
 * Where a picker comes out of a building: the middle of the side that looks toward `toward`
 * (the racks), half a metre out. The route start (P) goes here for an office.
 */
export function buildingDoor(b: FloorBuilding, toward: { readonly x: number; readonly y: number }): Coordinates3D {
  const dx = toward.x - b.x;
  const dy = toward.y - b.y;
  // compare against the half sizes, so a long building opens on its long side toward the racks
  const alongX = Math.abs(dx) / (b.width / 2) >= Math.abs(dy) / (b.depth / 2);
  if (alongX) return { x: round3(b.x + Math.sign(dx || 1) * (b.width / 2 + 0.5)), y: round3(b.y), z: 0 };
  return { x: round3(b.x), y: round3(b.y + Math.sign(dy || 1) * (b.depth / 2 + 0.5)), z: 0 };
}

/** The racks of `planned` that stand on floor already taken by others (same address excluded). */
export function overlappingRacks(planned: readonly RackGeometry[], existing: readonly RackGeometry[]): RackGeometry[] {
  const same = (a: RackGeometry, b: RackGeometry) => a.aisle === b.aisle && a.rack === b.rack;
  const others = existing.map((r) => ({ r, f: rackFootprint(r) }));
  return planned.filter((p) => {
    const f = rackFootprint(p);
    return others.some((o) => !same(o.r, p) && footprintsOverlap(f, o.f));
  });
}

/** Racks of rows and areas moved on the floor. */
export function moveRacks(racks: readonly RackGeometry[], dx: number, dy: number): RackGeometry[] {
  return racks.map((r) => ({ ...r, x: round3(r.x + dx), y: round3(r.y + dy) }));
}

/** Rows turned to face the other side (the walkway behind them); areas turned by 90°. */
export function turnRacks(racks: readonly RackGeometry[]): RackGeometry[] {
  return racks.map((r) => ({ ...r, facing: ((r.facing + (r.kind === 'area' ? 90 : 180)) % 360) as Facing }));
}

/** New sizes for one row (or area line): the open side and the start of the first position stay put. */
export interface RowSize {
  readonly kind?: RackKind;
  readonly width?: number;
  readonly depth?: number;
  readonly levelHeight?: number;
  readonly gap?: number;
  /** width of the walkway in front */
  readonly walkway?: number;
}

/** The gap between neighbouring positions of a row as it stands (0 for a single position). */
export function rowGap(row: readonly RackGeometry[]): number {
  const sorted = [...row].sort((a, b) => a.rack - b.rack);
  const first = sorted[0];
  const second = sorted[1];
  if (!first || !second) return 0;
  const step = Math.hypot(second.x - first.x, second.y - first.y) / Math.max(1, second.rack - first.rack);
  return Math.max(0, round3(step - first.width));
}

/**
 * Re-lays one row with new sizes: positions stay in number order along the row, the first one's
 * outer edge and the line of the open sides (so the walkway) stay where they were.
 */
export function resizeRow(row: readonly RackGeometry[], change: RowSize): RackGeometry[] {
  const sorted = [...row].sort((a, b) => a.rack - b.rack);
  const first = sorted[0];
  if (!first) return [];
  const last = sorted.at(-1)!;
  const n = NORMAL[first.facing];
  // along the row, square to the open side: toward the higher numbers (a lone position: to the left)
  const left = { x: -n.y, y: n.x };
  const dir = (last.x - first.x) * left.x + (last.y - first.y) * left.y < -1e-6 ? { x: -left.x, y: -left.y } : left;
  const width = change.width ?? first.width;
  const depth = change.depth ?? first.depth;
  const gap = change.gap ?? rowGap(sorted);
  const start = first.x * dir.x + first.y * dir.y - first.width / 2;
  const face = first.x * n.x + first.y * n.y + first.depth / 2;
  const across = face - depth / 2;
  return sorted.map((r) => {
    const along = start + (r.rack - first.rack) * (width + gap) + width / 2;
    return {
      ...r,
      kind: change.kind ?? r.kind,
      x: round3(dir.x * along + n.x * across),
      y: round3(dir.y * along + n.y * across),
      facing: first.facing,
      width,
      depth,
      levelHeight: change.levelHeight ?? r.levelHeight,
      reach: change.walkway !== undefined ? round3(change.walkway / 2) : r.reach,
    };
  });
}

/** A bin on the floor, as the builder knows it before its rack has a stored size. */
export interface LocatedBin {
  readonly aisle: string;
  readonly rack: number;
  readonly level: number;
  readonly type: LocationType;
  readonly at: Coordinates3D;
}

/**
 * Sizes for racks built before they were stored: the shelf stood 25 cm beside the bins'
 * coordinates on the far side of the aisle axis, as wide as the rack spacing allowed.
 */
export function inferRackGeometry(bins: readonly LocatedBin[], aisleAxis: 'x' | 'y'): RackGeometry[] {
  const along = (p: Coordinates3D) => (aisleAxis === 'y' ? p.y : p.x);
  const across = (p: Coordinates3D) => (aisleAxis === 'y' ? p.x : p.y);
  const step = (values: readonly number[], fallback: number) => {
    const sorted = [...new Set(values.map(round3))].sort((a, b) => a - b);
    const steps = sorted.slice(1).map((v, i) => v - sorted[i]!).filter((d) => d > 0.05);
    return steps.length ? Math.min(...steps) : fallback;
  };
  const spacing = new Map<string, number>();
  for (const aisle of new Set(bins.map((b) => b.aisle))) {
    spacing.set(aisle, step(bins.filter((b) => b.aisle === aisle && (b.type === 'pick' || b.type === 'bulk')).map((b) => along(b.at)), 2));
  }
  const groups = new Map<string, LocatedBin[]>();
  for (const b of bins) groups.set(`${b.aisle}|${b.rack}`, [...(groups.get(`${b.aisle}|${b.rack}`) ?? []), b]);
  const out: RackGeometry[] = [];
  for (const group of groups.values()) {
    const first = group[0]!;
    const mean = (f: (p: Coordinates3D) => number) => group.reduce((s, b) => s + f(b.at), 0) / group.length;
    const storage = group.some((b) => b.type === 'pick' || b.type === 'bulk');
    if (!storage) {
      out.push({ aisle: first.aisle, rack: first.rack, kind: 'area', x: round3(mean((p) => p.x)), y: round3(mean((p) => p.y)), facing: aisleAxis === 'y' ? 180 : 270, width: 0.9, depth: 0.9, levelHeight: 0, reach: 0 });
      continue;
    }
    const preset = RACK_PRESETS[group.every((b) => b.type === 'bulk') ? 'pallet_rack' : 'shelf'];
    const reach = 0.25;
    const width = round3(Math.min(preset.width, spacing.get(first.aisle)! * 0.9));
    const a = mean(along);
    const c = mean(across) + reach + preset.depth / 2;
    out.push({
      aisle: first.aisle,
      rack: first.rack,
      kind: preset.kind,
      x: round3(aisleAxis === 'y' ? c : a),
      y: round3(aisleAxis === 'y' ? a : c),
      facing: aisleAxis === 'y' ? 180 : 270,
      width,
      depth: preset.depth,
      levelHeight: round3(step(group.map((b) => b.at.z), preset.levelHeight)),
      reach,
    });
  }
  return out;
}

/** Why a rack's size cannot be stored (empty = fine). */
export function rackProblems(r: RackGeometry): string[] {
  const problems: string[] = [];
  const name = `${r.aisle}-${String(r.rack).padStart(2, '0')}`;
  if (!CODE.test(r.aisle) || !Number.isInteger(r.rack) || r.rack < 1 || r.rack > 99) problems.push(`Geçersiz raf: ${name}`);
  if (!(RACK_KINDS as readonly string[]).includes(r.kind)) problems.push(`${name}: türü geçersiz`);
  if (!(FACINGS as readonly number[]).includes(r.facing)) problems.push(`${name}: yönü 0, 90, 180 veya 270 olmalı`);
  if (!between(r.x, -10_000, 10_000) || !between(r.y, -10_000, 10_000)) problems.push(`${name}: konumu sayı olmalı`);
  if (!(r.width > 0 && r.width <= 50) || !(r.depth > 0 && r.depth <= 50)) problems.push(`${name}: genişlik ve derinlik 0–50 m arasında olmalı`);
  if (!between(r.levelHeight, 0, 20) || !between(r.reach, 0, 20)) problems.push(`${name}: kat yüksekliği ve koridor 0–20 m arasında olmalı`);
  return problems;
}
